"use client";

import { useActionState, useRef, useState } from "react";
import { ArrowRight, Check, Copy, FlaskConical, LoaderCircle, ShieldCheck } from "lucide-react";
import { login, verifyLoginCode, type LoginState } from "@/app/actions/auth";
import { buttonClass } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { inputClass, labelClass } from "./fields";
import { PasswordInput } from "./PasswordInput";

export type DemoAccountRow = {
  key: string;
  label: string;
  email: string;
  password: string;
  name: string;
  company: string;
  group: "cliente" | "equipo";
};

/**
 * Ingreso con correo y contraseña (app/actions/auth.ts) y, si la cuenta la tiene activa, el código de la
 * verificación en dos pasos. En modo demostración, el panel "Cuentas de demostración" rellena el formulario.
 */
export function LoginForm({ next, accounts }: { next?: string; accounts: DemoAccountRow[] }) {
  const [state, action, pending] = useActionState<LoginState, FormData>(login, undefined);
  if (state?.twoFactor) return <CodeStep next={next} email={state.email} />;
  return (
    <PasswordStep next={next} accounts={accounts} state={state} action={action} pending={pending} />
  );
}

function CodeStep({ next, email }: { next?: string; email?: string }) {
  const [state, action, pending] = useActionState<LoginState, FormData>(verifyLoginCode, {
    twoFactor: true,
  });
  return (
    <form action={action} className="flex flex-col gap-5" noValidate>
      {next ? <input type="hidden" name="next" value={next} /> : null}
      <div className="flex items-start gap-3 rounded-lg border border-line bg-white px-4 py-3.5">
        <ShieldCheck aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-moss" />
        <p className="text-[14px] leading-snug text-text-2">
          Abre tu aplicación de autenticación y escribe el código de 6 dígitos de Avala
          {email ? (
            <>
              {" "}
              para <span className="font-semibold text-ink">{email}</span>
            </>
          ) : null}
          .
        </p>
      </div>
      <div className="flex flex-col gap-2">
        <label htmlFor="code" className={labelClass}>
          Código de verificación
        </label>
        <input
          id="code"
          name="code"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={7}
          autoFocus
          required
          placeholder="000000"
          aria-invalid={state?.error ? true : undefined}
          aria-describedby={state?.error ? "code-error" : undefined}
          className={cn(inputClass, "num text-center text-[20px] tracking-[0.4em]")}
        />
      </div>
      {state?.error ? (
        <p
          id="code-error"
          role="alert"
          className="rounded-md bg-danger-tint px-3.5 py-2.5 text-[13.5px] leading-snug font-medium text-danger"
        >
          {state.error}
        </p>
      ) : null}
      <button
        type="submit"
        disabled={pending}
        className={buttonClass("primary", "lg", "mt-1 w-full")}
      >
        {pending ? <LoaderCircle aria-hidden="true" className="size-4 animate-spin" /> : null}
        {pending ? "Verificando…" : "Verificar e ingresar"}
      </button>
      <a
        href="/login"
        className="self-center text-[13px] font-semibold text-moss underline-offset-4 hover:text-forest hover:underline"
      >
        Volver al ingreso
      </a>
    </form>
  );
}

function PasswordStep({
  next,
  accounts,
  state,
  action,
  pending,
}: {
  next?: string;
  accounts: DemoAccountRow[];
  state: LoginState;
  action: (formData: FormData) => void;
  pending: boolean;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [picked, setPicked] = useState<string | null>(null);
  const submitRef = useRef<HTMLButtonElement>(null);

  const emailError = state?.field === "email" ? state.error : undefined;
  const passwordError = state?.field === "password" ? state.error : undefined;

  function fill(a: DemoAccountRow) {
    setEmail(a.email);
    setPassword(a.password);
    setPicked(a.key);
    requestAnimationFrame(() => submitRef.current?.focus());
  }

  return (
    <div className="flex flex-col gap-8">
      <form action={action} className="flex flex-col gap-5" noValidate>
        {next ? <input type="hidden" name="next" value={next} /> : null}
        <div className="flex flex-col gap-2">
          <label htmlFor="email" className={labelClass}>
            Correo
          </label>
          <input
            id="email"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="nombre@empresa.com"
            aria-invalid={emailError ? true : undefined}
            aria-describedby={emailError ? "login-error" : undefined}
            className={inputClass}
          />
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex items-baseline justify-between gap-3">
            <label htmlFor="password" className={labelClass}>
              Contraseña
            </label>
            <a
              href="/recuperar"
              className="text-[13px] font-semibold text-moss underline-offset-4 hover:text-forest hover:underline"
            >
              ¿Olvidaste tu contraseña?
            </a>
          </div>
          <PasswordInput
            id="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-invalid={passwordError ? true : undefined}
            aria-describedby={passwordError ? "login-error" : undefined}
          />
        </div>
        {state?.error ? (
          <p
            id="login-error"
            role="alert"
            className="rounded-md bg-danger-tint px-3.5 py-2.5 text-[13.5px] leading-snug font-medium text-danger"
          >
            {state.error}
          </p>
        ) : null}
        <button
          ref={submitRef}
          type="submit"
          disabled={pending}
          className={buttonClass("primary", "lg", "mt-1 w-full")}
        >
          {pending ? <LoaderCircle aria-hidden="true" className="size-4 animate-spin" /> : null}
          {pending ? "Ingresando…" : "Ingresar"}
          {!pending ? <ArrowRight aria-hidden="true" className="size-4" /> : null}
        </button>
      </form>

      {accounts.length ? (
        <section
          aria-labelledby="demo-title"
          className="rounded-xl border border-dashed border-gold/80 bg-[#FBF8EF]"
        >
          <header className="flex items-start gap-3 px-4 pt-4 pb-3">
            <span className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-full bg-gold/25 text-gold-text">
              <FlaskConical aria-hidden="true" className="size-3.5" />
            </span>
            <div className="flex flex-col gap-0.5">
              <h2 id="demo-title" className="text-sm font-semibold text-gold-text">
                Cuentas de demostración
              </h2>
              <p className="text-[12.5px] leading-snug text-text-3">
                Clientes ficticios, uno por etapa, y el equipo interno que los atiende. Elige uno
                para llenar el formulario.
              </p>
            </div>
          </header>
          {(["cliente", "equipo"] as const).map((group) => (
            <div key={group}>
              <p className="border-t border-dashed border-gold/50 bg-gold/[0.06] px-4 py-1.5 text-[11.5px] font-semibold tracking-[0.05em] text-gold-text uppercase">
                {group === "cliente" ? "Clientes · portal" : "Equipo Avala · consola"}
              </p>
              <ul className="flex flex-col border-t border-dashed border-gold/50">
                {accounts
                  .filter((a) => a.group === group)
                  .map((a) => {
                    const active = picked === a.key;
                    return (
                      <li
                        key={a.key}
                        className="border-b border-dashed border-gold/40 last:border-b-0"
                      >
                        <button
                          type="button"
                          onClick={() => fill(a)}
                          aria-pressed={active}
                          className={cn(
                            "group flex w-full items-center gap-3 px-4 py-3 text-left transition-colors duration-200 hover:bg-gold/10 focus-visible:bg-gold/10",
                            active && "bg-gold/12",
                          )}
                        >
                          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                            <span className="text-[13.5px] font-semibold text-ink">{a.label}</span>
                            <span className="truncate text-[12.5px] text-text-3">
                              {a.name} · {a.company}
                            </span>
                            <span className="num mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[12px] text-text-2">
                              <span className="truncate">{a.email}</span>
                              <span>
                                <span className="text-text-3">Contraseña </span>
                                <span className="font-semibold">{a.password}</span>
                              </span>
                            </span>
                          </span>
                          <span
                            className={cn(
                              "inline-flex h-8 shrink-0 items-center gap-1.5 rounded-sm border px-2.5 text-[12.5px] font-semibold transition-colors",
                              active
                                ? "border-forest bg-forest text-sand"
                                : "border-gold/70 text-gold-text group-hover:border-gold-deep",
                            )}
                          >
                            {active ? (
                              <Check aria-hidden="true" className="size-3.5" />
                            ) : (
                              <Copy aria-hidden="true" className="size-3.5" />
                            )}
                            {active ? "Lista" : "Usar"}
                          </span>
                        </button>
                      </li>
                    );
                  })}
              </ul>
            </div>
          ))}
        </section>
      ) : null}
    </div>
  );
}
